import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  memo,
  useMemo,
} from "react";
import {
  TextField,
  Button,
  List,
  ListItem,
  FormControl,
  Popover,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { UserContext } from "../../../../../platform/components/auth/auth-context";
import { rootClientPath } from "../../../../../platform/util/link";
import { useSelectedBusiness } from "../../../../../platform/redux/store";
import {
  SEND_EMAIL_MUTATION,
  UPDATE_PAYPAL_SUBSCRIPTION,
} from "../../../../../platform/graphql/mutations";

// Generic dialog wrapper
const GenericDialog = ({ open, handleClose, title, content, actions }) => (
  <Dialog open={open} onClose={handleClose}>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>{content}</DialogContent>
    <DialogActions>{actions}</DialogActions>
  </Dialog>
);

// Core content of the staff dialog
const AddEditStaffContent = memo(
  ({
    username,
    setUsername,
    initials,
    setInitials,
    url,
    setUrl,
    email,
    setEmail,
    role,
    handlePermissionClick,
    anchorEl,
    isPopoverOpen,
    selectedPermission,
    handlePermissionChange,
    selectedBusiness,
    isEdit,
    usernameError,
    emailError,
    initialsError,
    initialsHelperText,
    urlError,
  }) => (
    <>
      <TextField
        label="Name"
        value={username}
        variant="outlined"
        fullWidth
        onChange={(e) => setUsername(e.target.value)}
        error={usernameError}
        helperText={usernameError ? "Name is required" : ""}
        style={{ marginBottom: "16px", marginTop: "16px" }}
      />
      <TextField
        label="Email"
        value={email}
        variant="outlined"
        fullWidth
        onChange={(e) => setEmail(e.target.value)}
        error={emailError}
        helperText={emailError ? "Invalid email address" : ""}
        style={{ marginBottom: "16px" }}
      />
      <TextField
        label="Initials"
        value={initials}
        variant="outlined"
        fullWidth
        onChange={(e) =>
          setInitials(e.target.value.toUpperCase().replace(/\s+/g, ""))
        }
        inputProps={{ maxLength: 2 }}
        error={initialsError}
        helperText={initialsHelperText}
        style={{ marginBottom: "16px" }}
      />
      {isEdit && (
        <TextField
          label="URL"
          value={url}
          variant="outlined"
          fullWidth
          onChange={(e) => setUrl(e.target.value)}
          error={urlError}
          helperText={
            urlError
              ? "Invalid URL: only letters, numbers, hyphens (-), underscores (_), and dots (.) are allowed"
              : ""
          }
          style={{ marginBottom: "16px", marginTop: "16px" }}
        />
      )}
      <FormControl fullWidth>
        <TextField
          label="Role"
          variant="outlined"
          onClick={handlePermissionClick}
          aria-describedby={anchorEl ? "role-popover" : undefined}
          fullWidth
          InputProps={{
            readOnly: true,
            endAdornment: (
              <div>
                {isPopoverOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </div>
            ),
          }}
          value={selectedPermission ? selectedPermission.name : role}
          style={{ marginBottom: "16px" }}
        />
        <Popover
          id="role-popover"
          open={isPopoverOpen}
          anchorEl={anchorEl}
          onClose={() => handlePermissionChange(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <List>
            {selectedBusiness?.roles?.map((roleItem) => (
              <ListItem
                key={roleItem.id}
                button
                onClick={() => handlePermissionChange(roleItem)}
              >
                <ListItemText
                  primary={roleItem.name}
                  secondary={roleItem.description}
                />
              </ListItem>
            ))}
          </List>
        </Popover>
      </FormControl>
      <Alert severity="info" sx={{ mt: 1 }}>
        Use the <strong>Admin</strong> role for someone who needs to manage this business without being the owner.
      </Alert>
    </>
  )
);

const AddEditStaffStyledDialog = ({
  open,
  handleClose,
  staff,
  handleSaveUpdateStaff,
}) => {
  const [username, setUsername] = useState("");
  const [initials, setInitials] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(3);
  const [url, setUrl] = useState("");
  const [role, setRole] = useState("");
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const selectedBusiness = useSelectedBusiness();
  const [selectedPermission, setSelectedPermission] = useState(null);
  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser;
  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);
  // const [updatePaypalSubscription] = useMutation(UPDATE_PAYPAL_SUBSCRIPTION);
  const prevOpenRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const getFriendlyErrorMessage = (error) => {
    const message =
      error?.message ||
      error?.graphQLErrors?.[0]?.message ||
      "Failed to save staff. Please try again.";
    if (String(message).toLowerCase().includes("already a staff member")) {
      return "This user is already a staff member for this business.";
    }
    return message;
  };

  // Initialize fields
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      if (staff) {
        setUsername(staff?.user?.username ?? "");
        setInitials(staff?.initials ?? "");
        setUrl(staff?.url ?? "");
        setEmail(staff?.user?.email || "");
        setRole(staff?.role?.name ?? "");
        setStatus(staff?.status ?? "");
      } else {
        setUsername("");
        setInitials("");
        setUrl("");
        setEmail("");
        setRole("");
        setStatus(3);
        setSelectedPermission(null);
      }
      setSaveError("");
      setSaving(false);
    }
    prevOpenRef.current = open;
  }, [open, staff]);

  // Validations
  const isEmailValid = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
    [email]
  );
  const isUrlValid = useMemo(
    () => !staff || /^[a-zA-Z0-9._-]*$/.test(url.trim()),
    [url, staff]
  );
  const isRoleValid = staff ? true : selectedPermission !== null;
  const normalizedInitials = String(initials || "").trim().toUpperCase();
  const initialsRequiredError = normalizedInitials === "";
  const initialsLengthError =
    normalizedInitials !== "" && normalizedInitials.length !== 2;
  const initialsDuplicateError = useMemo(() => {
    if (!normalizedInitials) return false;
    const allStaffs = selectedBusiness?.businessStaffs || [];
    return allStaffs.some((s) => {
      if (staff?.id && Number(s?.id) === Number(staff.id)) return false;
      return String(s?.initials || "").trim().toUpperCase() === normalizedInitials;
    });
  }, [normalizedInitials, selectedBusiness?.businessStaffs, staff?.id]);

  const usernameError = username.trim() === "";
  const emailError = email.trim() !== "" && !isEmailValid;
  const urlError = staff && url.trim() !== "" && !isUrlValid;
  const initialsError =
    initialsRequiredError || initialsLengthError || initialsDuplicateError;
  const initialsHelperText = initialsRequiredError
    ? "Initials are required"
    : initialsLengthError
      ? "Initials must be exactly 2 characters"
    : initialsDuplicateError
      ? "Initials must be unique in this business"
      : "";

  const isFormValid =
    !usernameError &&
    !emailError &&
    !urlError &&
    !initialsError &&
    isRoleValid;

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError("");
    const editedStaff = {
      ...staff,
      status,
      url,
      initials: String(initials || "").trim().toUpperCase(),
      user: {
        ...staff?.user,
        email,
        username,
      },
      role: {
        id: Number(selectedPermission?.id ?? staff?.role?.id),
        name: selectedPermission?.name ?? staff?.role?.name,
      },
    };

    let updatedStaff = null;
    let errorMessage = "";
    try {
      updatedStaff = await handleSaveUpdateStaff(editedStaff);
    } catch (error) {
      console.error("Error saving staff:", error);
      errorMessage = getFriendlyErrorMessage(error);
      setSaveError(errorMessage);
    } finally {
      setSaving(false);
    }

    if (!updatedStaff) {
      if (!errorMessage) {
        setSaveError("Failed to save staff. Please try again.");
      }
      return;
    }

    handleClose();

    if (updatedStaff) {
      const link = `${rootClientPath}/biz/approve/${updatedStaff?.staffApproveId}`;
      const ownerDisplayName =
        String(
          providerUser?.name ||
          providerUser?.userName ||
          providerUser?.username ||
          providerUser?.email ||
          ""
        ).trim() || "there";

      const emailBodyNewStaff = `
        <p>Hello, ${username}</p>
        <p>You're invited to join Plany365 system for ${selectedBusiness?.businessBasic.name}.</p>
        <p><a href="${link}">Click here to sign up</a>.</p>`;

      const emailBodyBusinessOwner = `
        <p>Hello, ${ownerDisplayName},</p>
        <p>New staff added to ${selectedBusiness?.businessBasic.name}. Billing has been updated.</p>`;
      // For residential care homes(till figured it out), we dont auto update billing when adding staff
      try {
        // await updatePaypalSubscription({
        //   variables: {
        //     externalSubscriptionId: providerUser.externalSubscriptionId,
        //     quantity: 1,
        //   },
        // });
        await sendEmail({
          variables: {
            from: providerUser.email,
            to: email,
            subject: `Join Plany365 for ${selectedBusiness?.businessBasic.name}`,
            text: `You're invited to Plany365 for ${selectedBusiness?.businessBasic.name}: ${link}`,
            html: emailBodyNewStaff,
          },
        });
        await sendEmail({
          variables: {
            to: providerUser.email,
            subject: `Staff Added: ${selectedBusiness?.businessBasic.name ?? ""}`,
            text: "Staff added and billing updated.",
            html: emailBodyBusinessOwner,
          },
        });
      } catch (error) {
        console.error("Email error:", error);
      }
    }
  };

  const handlePermissionClick = useCallback((e) => {
    setIsPopoverOpen(true);
    setAnchorEl(e.currentTarget);
  }, []);

  const handlePermissionChange = useCallback((role) => {
    setSelectedPermission(role);
    setIsPopoverOpen(false);
    setAnchorEl(null);
  }, []);

  return (
    <GenericDialog
      open={open}
      handleClose={handleClose}
      title={staff ? "Edit Team Member" : "Add Team Member"}
      content={
        <>
          {saveError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          ) : null}
          <AddEditStaffContent
            username={username}
            setUsername={setUsername}
            usernameError={usernameError}
            initials={initials}
            setInitials={setInitials}
            initialsError={initialsError}
            initialsHelperText={initialsHelperText}
            email={email}
            setEmail={setEmail}
            emailError={emailError}
            url={url}
            setUrl={setUrl}
            urlError={urlError}
            role={role}
            handlePermissionClick={handlePermissionClick}
            anchorEl={anchorEl}
            isPopoverOpen={isPopoverOpen}
            selectedPermission={selectedPermission}
            handlePermissionChange={handlePermissionChange}
            selectedBusiness={selectedBusiness}
            isEdit={!!staff}
          />
        </>
      }
      actions={
        <>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            disabled={!isFormValid || saving}
          >
            {saving ? <CircularProgress size={18} /> : staff ? "Save" : "Add Team Member"}
          </Button>
        </>
      }
    />
  );
};

export default AddEditStaffStyledDialog;
